import { ReactElement, useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import { FieldArray, getIn, useFormikContext } from 'formik'

import { Loader } from '@10xLabs/react-web/components/Loader'
import { UsersIcon } from 'components/Icons/UsersIcon'
import { CancelIcon } from 'components/Icons/CancelIcon'
import { InputNumber } from 'components/InputNumber'
import { Button } from 'components/v2/Button'
import EmptyState from 'components/v2/EmptyState'
import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { Label } from 'components/v2/Label'
import { Select } from 'components/v2/Select'
import { ValidationWrapper } from 'components/v2/ValidationWrapper'
import { usePassengerTypesAutocomplete } from 'features/PassengerTypes/hooks'
import { usePassengerPropertiesAutocomplete } from 'features/PassengerProperties/hooks'
import { getCapacityLimits } from 'models/services'
import { ICapacityLimit, ICapacityLimitCardProps, ICapacityLimitSelectorProps, ICapacityLimitsForm } from './types'
import { PASSENGER_TYPE } from 'features/Bookings/components/PassengerType/constants'

const messages = defineMessages({
  passengerLabel: {
    id: 'services.form.capacityLimits.passenger.label',
    defaultMessage: 'Passenger'
  },
  passengerPlaceholder: {
    id: 'services.form.capacityLimits.passenger.placeholder',
    defaultMessage: 'Select a passenger'
  },
  propertyLabel: {
    id: 'services.form.capacityLimits.property.label',
    defaultMessage: 'Property'
  },
  propertyPlaceholder: {
    id: 'services.form.capacityLimits.property.placeholder',
    defaultMessage: 'Select a property'
  }
})

const CapacityLimitsContainer = styled.div<TSFixMe>`
  display: flex;
  gap: 24px;
  min-height: 128px;
`

const CapacityLimitsColumn = styled.div<TSFixMe>`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 16px;
`

const CapacityLimitsInfoCard = styled.div<TSFixMe>`
  background-color: #f8f8f8;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  width: calc(288px - 32px);
`

const CapacityLimitsInfoCardHeader = styled.div<TSFixMe>`
  align-items: center;
  display: flex;
  gap: 8px;
`

const LimitRow = styled.div<TSFixMe>`
  align-items: center;
  display: flex;
  justify-content: space-between;
`

const LimitLabel = styled(Label)<TSFixMe>`
  color: #717171;
  text-transform: capitalize;
  width: 100px;
`

const LimitInput = styled(InputNumber)<TSFixMe>`
  width: 48px;
`

const LimitActionContainer = styled.div<TSFixMe>`
  align-items: center;
  cursor: pointer;
  display: flex;
`

const StyledUsersIcon = styled(UsersIcon)<TSFixMe>`
  fill: #a86eeb;
  height: 24px;
  width: 24px;
`

const CapacityLimitsSelector = ({ name, label, placeholder, values }: ICapacityLimitSelectorProps): ReactElement => {
  const { handleBlur, touched, errors } = useFormikContext()
  const selectableValues = values.filter((passengerType): TSFixMe => !passengerType.isVisible)

  return (
    <FieldArray name={name}>
      {({ replace }): ReactElement => (
        <CapacityLimitsColumn>
          <Select
            label={label}
            onChange={(e): void => {
              const index = values.findIndex((passengerType): TSFixMe => passengerType.key === e.target.value)
              if (index >= 0) {
                replace(index, { ...values[index], isVisible: true })
              }
            }}
            optional
          >
            <option value="">{placeholder}</option>
            {selectableValues.map(
              (passengerType): TSFixMe => (
                <option key={passengerType.key} value={passengerType.key}>
                  {passengerType.key}
                </option>
              )
            )}
          </Select>
          {values.map((passenger, index): TSFixMe => {
            if (!passenger.isVisible) return null

            const limitFormKey = `${name}.${index}.value`
            return (
              <ValidationWrapper
                key={`passenger-${passenger.key}`}
                touched={getIn(touched, limitFormKey)}
                errors={getIn(errors, limitFormKey)}
              >
                {(isInvalid): ReactElement => (
                  <LimitRow>
                    <LimitLabel>{passenger.key}</LimitLabel>
                    <LimitInput
                      id={limitFormKey}
                      name={limitFormKey}
                      value={passenger.value ?? ''}
                      onChange={(value: string): void => replace(index, { ...passenger, value })}
                      onBlur={handleBlur}
                      isInvalid={isInvalid}
                      small
                    />
                    <LimitActionContainer onClick={(): void => replace(index, { ...passenger, isVisible: false })}>
                      <CancelIcon />
                    </LimitActionContainer>
                  </LimitRow>
                )}
              </ValidationWrapper>
            )
          })}
        </CapacityLimitsColumn>
      )}
    </FieldArray>
  )
}

const hasPetLimitDifferentThanZero = (limits: ICapacityLimit[]): boolean => {
  return limits.some((limit) => limit.key === PASSENGER_TYPE.PET && limit.value !== 0)
}

export const CapacityLimitsCard = ({ isServicePetFriendly }: ICapacityLimitCardProps): ReactElement => {
  const intl = useIntl()
  const { values, resetForm, setValues } = useFormikContext<ICapacityLimitsForm>()

  const {
    data: passengerTypesData,
    loading: passengerTypesLoading,
    error: passengerTypesError,
    refetch: refetchPassengerTypes
  } = usePassengerTypesAutocomplete({
    queryInput: { enabled: true }
  })
  const {
    data: passengerPropertiesData,
    loading: passengerPropertiesLoading,
    error: passengerPropertiesError,
    refetch: refetchPassengerProperties
  } = usePassengerPropertiesAutocomplete({
    queryInput: { enabled: true }
  })

  const findDefinedLimits = (limits: ICapacityLimit[], passengers: ICapacityLimit[]): ICapacityLimit[] => {
    return passengers.map((passenger): TSFixMe => {
      if (!isServicePetFriendly && passenger.key === PASSENGER_TYPE.PET) {
        return {
          ...passenger,
          value: 0,
          isVisible: false
        }
      }
      const limitIndex = limits.findIndex((limit): TSFixMe => limit.key === passenger.key)
      return {
        ...passenger,
        value: limitIndex >= 0 ? limits[limitIndex].value : undefined,
        isVisible: limitIndex >= 0
      }
    })
  }

  useEffect(() => {
    if (passengerTypesLoading || passengerPropertiesLoading) return

    const { passengers, properties } = getCapacityLimits(
      passengerTypesData?.passengerTypesAutocomplete,
      passengerPropertiesData?.passengerPropertiesAutocomplete
    )
    const limits = values.limits.filter((limit) => limit.isVisible)
    const passengersLimits = findDefinedLimits(limits, passengers)
    const propertiesLimits = findDefinedLimits(limits, properties)

    if (!isServicePetFriendly && hasPetLimitDifferentThanZero(values.limits)) {
      setValues({
        ...values,
        passengers: passengersLimits,
        properties: propertiesLimits
      })
    } else {
      resetForm({
        values: {
          ...values,
          passengers: passengersLimits,
          properties: propertiesLimits
        }
      })
    }
  }, [values.limits, passengerTypesData, passengerPropertiesData])

  useEffect(() => {
    if (passengerTypesLoading || passengerPropertiesLoading) return

    setValues({
      ...values,
      passengers: values.passengers.map((passengerLimit) => {
        if (passengerLimit.key === PASSENGER_TYPE.PET) {
          if (!isServicePetFriendly) {
            return {
              ...passengerLimit,
              value: 0,
              isVisible: false
            }
          } else {
            return {
              ...passengerLimit,
              value: undefined,
              isVisible: false
            }
          }
        }
        return passengerLimit
      })
    })
  }, [isServicePetFriendly])

  const passengersLoading = passengerTypesLoading || passengerPropertiesLoading
  const passengersError = passengerTypesError || passengerPropertiesError

  if (passengersLoading) {
    return (
      <FormLayout.Card>
        <EmptyState.Container>
          <Loader />
        </EmptyState.Container>
      </FormLayout.Card>
    )
  } else if (passengersError) {
    return (
      <FormLayout.Card>
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="settings.form.error.description"
              defaultMessage="There was an error while loading your passenger types and properties"
            />
          </EmptyState.Description>
          <EmptyState.Action>
            <Button
              onClick={(): void => {
                refetchPassengerTypes()
                refetchPassengerProperties()
              }}
              kind="primary"
            >
              <FormattedMessage
                id="settings.form.error.action"
                defaultMessage="Reload your passenger types and properties"
              />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      </FormLayout.Card>
    )
  }
  const passengerTypesOptions = isServicePetFriendly
    ? values.passengers
    : values.passengers.filter((passenger) => passenger.key !== PASSENGER_TYPE.PET)
  return (
    <FormLayout.Card>
      <FormLayout.CardHeader>
        <Heading.H4>
          <FormattedMessage id="services.form.section.capacityLimits.title" defaultMessage="Capacity Limits" />
        </Heading.H4>
      </FormLayout.CardHeader>
      <CapacityLimitsContainer>
        <CapacityLimitsSelector
          name="passengers"
          label={intl.formatMessage(messages.passengerLabel)}
          placeholder={intl.formatMessage(messages.passengerPlaceholder)}
          values={passengerTypesOptions}
        />
        <CapacityLimitsSelector
          name="properties"
          label={intl.formatMessage(messages.propertyLabel)}
          placeholder={intl.formatMessage(messages.propertyPlaceholder)}
          values={values.properties}
        />
        <CapacityLimitsInfoCard>
          <CapacityLimitsInfoCardHeader>
            <StyledUsersIcon />
            <Heading.H4>
              <FormattedMessage id="services.form.section.capacityLimitsInfo.title" defaultMessage="Important" />
            </Heading.H4>
          </CapacityLimitsInfoCardHeader>
          <Heading.Body>
            <FormattedMessage
              id="services.form.section.capacityLimitsInfo.description"
              defaultMessage="If you limit an adult passenger, {br} it will affect the capacity {br} of service."
              values={{
                br: <br />
              }}
            />
          </Heading.Body>
        </CapacityLimitsInfoCard>
      </CapacityLimitsContainer>
    </FormLayout.Card>
  )
}
